/**
 * Widget Skeleton Component
 *
 * Loading skeleton placeholders for widgets.
 * Shows appropriate skeleton layout based on widget type.
 *
 * @author BizClick WMS
 * @version 1.0.0
 */

import React from 'react';

interface WidgetSkeletonProps {
  /** Skeleton variant matching widget type */
  variant?: 'stat' | 'chart' | 'list' | 'grid' | 'gauge';
  /** Number of skeleton items (for list/grid variants) */
  count?: number;
  /** Custom class name */
  className?: string;
}

/**
 * Animated skeleton pulse block
 */
const Pulse: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`bg-gray-700 rounded animate-pulse ${className}`} />
);

const WidgetSkeleton: React.FC<WidgetSkeletonProps> = ({
  variant = 'stat',
  count = 3,
  className = '',
}) => {
  switch (variant) {
    case 'stat':
      return (
        <div className={`space-y-3 ${className}`}>
          <Pulse className="h-3 w-20" />
          <Pulse className="h-8 w-32" />
          <Pulse className="h-3 w-16" />
        </div>
      );

    case 'chart':
      return (
        <div className={`space-y-3 ${className}`}>
          <div className="flex justify-between">
            <Pulse className="h-3 w-24" />
            <Pulse className="h-3 w-16" />
          </div>
          <div className="flex items-end gap-1 h-24">
            {[65, 40, 80, 55, 90, 35, 70, 50].map((h, i) => (
              <div
                key={i}
                className="flex-1 bg-gray-700 rounded animate-pulse"
                style={{ height: `${h}%` }}
              />
            ))}
          </div>
          <div className="flex justify-between">
            {Array.from({ length: 4 }).map((_, i) => (
              <Pulse key={i} className="h-2 w-8" />
            ))}
          </div>
        </div>
      );

    case 'list':
      return (
        <div className={`space-y-2 ${className}`}>
          {Array.from({ length: count }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 p-2">
              <Pulse className="w-8 h-8 rounded-full flex-shrink-0" />
              <div className="flex-1 space-y-1.5">
                <Pulse className="h-3 w-3/4" />
                <Pulse className="h-2 w-1/2" />
              </div>
              <Pulse className="w-12 h-4 rounded flex-shrink-0" />
            </div>
          ))}
        </div>
      );

    case 'grid':
      return (
        <div className={`grid grid-cols-3 gap-3 ${className}`}>
          {Array.from({ length: count }).map((_, i) => (
            <div key={i} className="bg-gray-700/30 rounded-lg p-3 space-y-2">
              <Pulse className="h-3 w-16" />
              <Pulse className="h-6 w-20" />
              <Pulse className="h-2 w-12" />
            </div>
          ))}
        </div>
      );

    case 'gauge':
      return (
        <div className={`flex items-center justify-center ${className}`}>
          <div className="relative">
            <Pulse className="w-24 h-24 rounded-full" />
            <div className="absolute inset-3">
              <Pulse className="w-full h-full rounded-full bg-gray-800" />
            </div>
          </div>
        </div>
      );

    default:
      return (
        <div className={`space-y-3 ${className}`}>
          <Pulse className="h-4 w-24" />
          <Pulse className="h-8 w-full" />
        </div>
      );
  }
};

export default WidgetSkeleton;
